import { Button } from 'flowbite-react';
import { useCallback, useEffect, useRef, useState } from 'react';

interface CalendlyAPI {
  initPopupWidget: (options: { url: string }) => void;
}

declare global {
  interface Window {
    Calendly?: CalendlyAPI;
  }
}

const CALENDLY_URL =
  'https://calendly.com/d/cj93-9f9-6gn?background_color=e9efff&primary_color=1a1a1a&hide_gdpr_banner=1';

const BRAND_LOGOS: { src: string; alt: string }[] = [
  { src: 'https://cdn.themavericklab.com/plg-obvi-logo.png', alt: 'Obvi' },
  { src: 'https://cdn.themavericklab.com/plg-particle-logo.png', alt: 'Particle' },
  { src: 'https://cdn.themavericklab.com/plg-brucebolt-logo.png', alt: 'Bruce Bolt' },
  { src: 'https://cdn.themavericklab.com/plg-drsquatch-logo.png', alt: 'Dr. Squatch' },
  { src: 'https://cdn.themavericklab.com/plg-manly-bands-logo.png', alt: 'Manly Bands' },
  { src: 'https://cdn.themavericklab.com/plg-azuna-logo.png', alt: 'Azuna' },
  { src: 'https://cdn.themavericklab.com/plg-collarsco-logo.png', alt: 'Collars & Co.' },
  { src: 'https://cdn.themavericklab.com/plg-drsam-logo.png', alt: 'Dr. Sam' },
  { src: 'https://cdn.themavericklab.com/plg-primesports-logo.png', alt: 'Prime Sports' },
  { src: 'https://cdn.themavericklab.com/plg-remi-logo.png', alt: 'Remi' },
  { src: 'https://cdn.themavericklab.com/plg-sassysaints-logo.png', alt: 'Sassy Saints' },
  { src: 'https://cdn.themavericklab.com/plg-tumble-logo.png', alt: 'Tumble' },
];

export function HomePage() {
  const [calendlyReady, setCalendlyReady] = useState(() => !!window.Calendly);
  const pendingClickRef = useRef(false);

  // Inject Calendly's popup widget script + CSS once. Track when the script finishes loading so a
  // pre-load click doesn't silently no-op — we queue the click and fire it once Calendly is ready.
  useEffect(() => {
    if (window.Calendly) {
      setCalendlyReady(true);
      return;
    }
    const cssId = 'calendly-widget-css';
    const scriptId = 'calendly-widget-script';
    if (!document.getElementById(cssId)) {
      const link = document.createElement('link');
      link.id = cssId;
      link.rel = 'stylesheet';
      link.href = 'https://assets.calendly.com/assets/external/widget.css';
      document.head.appendChild(link);
    }
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      document.body.appendChild(script);
    }
    const onLoad = () => {
      setCalendlyReady(true);
    };
    script.addEventListener('load', onLoad);
    return () => {
      script.removeEventListener('load', onLoad);
    };
  }, []);

  // If the user clicked before the script finished loading, fire the popup once Calendly attaches.
  useEffect(() => {
    if (calendlyReady && pendingClickRef.current && window.Calendly) {
      pendingClickRef.current = false;
      window.Calendly.initPopupWidget({ url: CALENDLY_URL });
    }
  }, [calendlyReady]);

  const triggerCalendlyPopup = useCallback(() => {
    if (window.Calendly) {
      window.Calendly.initPopupWidget({ url: CALENDLY_URL });
    } else {
      pendingClickRef.current = true;
    }
  }, []);

  return (
    <div className='bg-blue-50'>
      <section
        className='flex h-[80vh] bg-cover bg-fixed bg-no-repeat'
        style={{
          backgroundColor: 'hsla(216, 100%, 98%, 1)',
          backgroundImage:
            'url(https://uploads-ssl.webflow.com/61dfc899a471635beadca9b9/61dfc899a47163c304dcaa3f_blob-techplus-x-template.png)',
        }}
      >
        <div className='mx-auto grid max-w-screen-xl gap-12 px-6 md:h-[80vh] md:grid-cols-2 md:items-center'>
          <div className='place-self-center text-center md:text-left'>
            <h1 className='mb-4 text-3xl font-bold leading-none md:text-5xl xl:text-6xl'>
              Boost ecommerce LTV with personalized video marketing
            </h1>
            <p className='mb-6 mt-12 max-w-2xl font-normal text-gray-900 md:text-lg lg:mb-8 lg:text-xl'>
              Maverick&apos;s video platform helps ecommerce stores create personalized experiences for each of their
              customers across the user journey
            </p>
            <p className='mb-6 mt-12 max-w-2xl font-normal text-gray-900 md:text-lg lg:mb-8 lg:text-xl'>
              Increase conversion, boost LTV, and delight your customers while achieving 25X-50X ROI
            </p>
            <Button color='dark' size='xl' className='mt-6' onClick={triggerCalendlyPopup}>
              Learn more
              <svg
                className='ms-2 size-3.5 rtl:rotate-180'
                aria-hidden='true'
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 14 10'
              >
                <path
                  stroke='currentColor'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                  d='M1 5h12m0 0L9 1m4 4L9 9'
                />
              </svg>
            </Button>
          </div>
          <div className='hidden h-[80vh] place-self-center md:block'>
            <img
              src='https://cdn.themavericklab.com/plg-logo-grid-2.png'
              className='size-full object-cover'
              alt='Brand results grid'
            />
          </div>
        </div>
      </section>

      <section>
        <div className='mx-auto grid grid-cols-3 py-12 md:grid-cols-6'>
          {BRAND_LOGOS.map((logo) => (
            <div key={logo.src} className='mx-auto place-self-center'>
              <img src={logo.src} className='mx-auto h-auto w-auto md:h-24' alt={`${logo.alt} logo`} />
            </div>
          ))}
        </div>
      </section>

      <section className='bg-indigo-400'>
        <div className='grid grid-cols-1 gap-24 px-4 py-12 md:grid-cols-2 md:px-36'>
          <div className='mx-auto place-self-center'>
            <a
              href='https://themavericklab.com/videos/31db45a0-8d09-426b-848a-5f1c000f5dce/'
              target='_blank'
              rel='noreferrer'
            >
              <img
                src='https://cdn.themavericklab.com/plg-dr-squatch-video-play.png'
                className='mx-auto h-auto w-auto md:min-h-48'
                alt='Doctor Squatch Video'
              />
            </a>
          </div>
          <div className='mx-auto place-self-center'>
            <img
              src='https://cdn.themavericklab.com/plg-dr-squatch-plus-maverick.png'
              className='mx-auto h-auto w-auto md:min-h-32'
              alt='Doctor Squatch + Maverick'
            />
            <p className='md:text-md my-6 py-6 text-center font-normal md:text-left lg:text-lg'>
              See how Dr. Squatch gets a huge boost in email and SMS engagement using personalized videos with Maverick.
            </p>
            <Button
              as='a'
              color='dark'
              size='xl'
              href='https://www.trymaverick.com/case-studies/transcending-traditional-cx-dr-squatch-achieves-big-engagement-boost-using-ai-generated-personalized-videos'
            >
              Case Study
            </Button>
          </div>
        </div>
      </section>

      <footer className='m-4'>
        <div className='mx-auto w-full max-w-screen-xl p-4 md:flex md:items-center md:justify-between'>
          <span className='text-sm text-gray-500 sm:text-center'>
            © 2026{' '}
            <a href='https://www.trymaverick.com/' className='hover:underline'>
              The Maverick Lab Inc.
            </a>
            . All Rights Reserved.
          </span>
          <ul className='mt-3 flex flex-wrap items-center text-sm font-medium text-gray-500 sm:mt-0'>
            <li>
              <a href='https://www.trymaverick.com/pp' className='me-4 hover:underline md:me-6'>
                Privacy Policy
              </a>
            </li>
            <li>
              <a href='https://www.trymaverick.com/termsofservice' className='me-4 hover:underline md:me-6'>
                Terms of Service
              </a>
            </li>
          </ul>
        </div>
      </footer>
    </div>
  );
}
